import React, { useMemo, useState } from "react";
import { Alert, Box, Button, Divider, Stack, Typography } from "@mui/material";
import AutoFixHighRoundedIcon from "@mui/icons-material/AutoFixHighRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import {
  createAdminJobContext,
  repairSubmissionSelectFieldData,
} from "../../../helpers/AdminJobsHelpers";
import "./AdminJobsPanel.css";

const buildSummaryStats = (result) => [
  { key: "scoped", label: "Scoped", value: result.totalScoped, tone: "neutral" },
  { key: "updated", label: "Updated", value: result.updated, tone: "success" },
  { key: "unchanged", label: "Unchanged", value: result.unchanged, tone: "muted" },
  { key: "removedTheme", label: "Theme Removed", value: result.removedTheme || 0, tone: "info" },
  { key: "topicFromTheme", label: "Topic From Theme", value: result.populatedTopicFromTheme || 0, tone: "info" },
  { key: "topicAliases", label: "Topic Aliases", value: result.normalizedTopicFromAlias || 0, tone: "teal" },
  { key: "topicNormalized", label: "Topic Labels", value: result.normalizedTopicFromLabel || 0, tone: "teal" },
  { key: "specialityAliases", label: "Speciality Aliases", value: result.normalizedSpecialityFromAlias || 0, tone: "teal" },
  { key: "specialityNormalized", label: "Speciality Labels", value: result.normalizedSpecialityFromLabel || 0, tone: "teal" },
  { key: "presentationNormalized", label: "Presentation Labels", value: result.normalizedPresentationTypeFromLabel || 0, tone: "teal" },
  { key: "flaggedInvalid", label: "Flagged Invalid", value: result.flaggedInvalid || 0, tone: "warning" },
];

const formatListValue = (value) => {
  if (value === null || value === undefined || value === "") return "blank";
  return String(value);
};

const describeRowOutcome = (item) => {
  const changedFields = [];

  if (item.before?.topic !== item.after?.topic) changedFields.push("topic");
  if (item.before?.speciality !== item.after?.speciality) changedFields.push("speciality");
  if (item.before?.presentation_type !== item.after?.presentation_type) {
    changedFields.push("presentation");
  }

  if (changedFields.length > 0) {
    const reasons = [];
    if (item.actions?.removedTheme) reasons.push("remove theme");
    if (item.actions?.populatedTopicFromTheme) reasons.push("topic from theme");
    if (item.actions?.normalizedTopicFromAlias) reasons.push("topic alias -> value");
    if (item.actions?.normalizedTopicFromLabel) reasons.push("topic label -> value");
    if (item.actions?.normalizedSpecialityFromAlias) reasons.push("speciality alias -> value");
    if (item.actions?.normalizedSpecialityFromLabel) reasons.push("speciality label -> value");
    if (item.actions?.normalizedPresentationTypeFromLabel) {
      reasons.push("presentation label -> value");
    }

    return reasons.length > 0
      ? `Will change: ${changedFields.join(", ")} (${reasons.join("; ")})`
      : `Will change: ${changedFields.join(", ")}`;
  }

  if (Array.isArray(item.invalidFields) && item.invalidFields.length > 0) {
    return "Needs review: unknown value";
  }

  return "No change";
};

const downloadJsonReport = (result, mode) => {
  if (!result) return;

  const payload = {
    exportedAt: new Date().toISOString(),
    mode,
    result,
  };

  const blob = new Blob([JSON.stringify(payload, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `admin-job-${result.jobName || "select-field-repair"}-${mode}.json`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

const AdminJobsPanel = ({
  allSubmissions,
  currentProject,
  currentForm,
  assets,
  viewfieldsInstance,
  fields,
  onClose,
}) => {
  const [jobState, setJobState] = useState({
    running: false,
    mode: null,
    error: "",
    result: null,
  });

  const adminJobContext = useMemo(
    () =>
      createAdminJobContext({
        allSubmissions,
        currentProject,
        currentForm,
        assets,
        fieldWrapper: viewfieldsInstance,
        processedFields: fields,
        logger: console,
      }),
    [allSubmissions, currentProject, currentForm, assets, viewfieldsInstance, fields]
  );

  const runTopicRepairJob = async (dryRun) => {
    setJobState({
      running: true,
      mode: dryRun ? "dry-run" : "apply",
      error: "",
      result: null,
      });

    try {
      const result = await repairSubmissionSelectFieldData(adminJobContext, {
        dryRun,
      });

      setJobState({
        running: false,
        mode: dryRun ? "dry-run" : "apply",
        error: "",
        result,
      });
    } catch (error) {
      setJobState({
        running: false,
        mode: dryRun ? "dry-run" : "apply",
        error: error?.message || String(error),
        result: null,
      });
    }
  };

  const sampleRows = Array.isArray(jobState.result?.sample) ? jobState.result.sample : [];
  const invalidExamples = Array.isArray(jobState.result?.invalidExamples)
    ? jobState.result.invalidExamples
    : [];
  const summaryStats = jobState.result ? buildSummaryStats(jobState.result) : [];

  return (
    <div className="admin-jobs-panel">
      <div className="admin-jobs-panel__header">
        <div>
          <Typography variant="h6" className="admin-jobs-panel__title">
            Admin Jobs
          </Typography>
          <Typography variant="body2" className="admin-jobs-panel__subtitle">
            Run scoped maintenance jobs against the currently loaded submissions.
          </Typography>
        </div>
        <Button variant="outlined" size="small" onClick={onClose}>
          Close
        </Button>
      </div>

      <Divider className="admin-jobs-panel__divider" />

      <div className="admin-jobs-panel__job-card">
        <div className="admin-jobs-panel__job-head">
          <div className="admin-jobs-panel__job-icon">
            <AutoFixHighRoundedIcon fontSize="small" />
          </div>
          <div className="admin-jobs-panel__job-copy">
            <Typography variant="subtitle1" className="admin-jobs-panel__job-title">
              Repair submission select values
            </Typography>
            <Typography variant="body2" className="admin-jobs-panel__job-description">
              Validates `data.topic`, `data.speciality`, and `data.presentation_type` against
              the current option sets, uses `data.theme` for topic only when topic is blank,
              converts stored labels to canonical values when there is an exact label match,
              and flags anything unknown for manual review.
            </Typography>
          </div>
        </div>

        <Stack direction="row" spacing={1.5} className="admin-jobs-panel__actions">
          <Button
            variant="outlined"
            startIcon={<PlayArrowRoundedIcon />}
            disabled={jobState.running}
            onClick={() => runTopicRepairJob(true)}
          >
            Dry run
          </Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={<TaskAltRoundedIcon />}
            disabled={jobState.running}
            onClick={() => runTopicRepairJob(false)}
          >
            Apply changes
          </Button>
        </Stack>

        {jobState.running && (
          <Alert severity="info" className="admin-jobs-panel__alert">
            Running {jobState.mode === "apply" ? "apply" : "dry run"} for select-field repair...
          </Alert>
        )}

        {jobState.error && (
          <Alert severity="error" className="admin-jobs-panel__alert">
            {jobState.error}
          </Alert>
        )}

        {jobState.result && (
          <Box className="admin-jobs-panel__result">
            <div className="admin-jobs-panel__result-head">
              <Typography variant="subtitle2">
                {jobState.mode === "apply" ? "Apply result" : "Dry run result"}
              </Typography>
              <div className="admin-jobs-panel__stat-grid">
                {summaryStats.map((stat) => (
                  <div
                    key={stat.key}
                    className={`admin-jobs-panel__stat admin-jobs-panel__stat--${stat.tone}`}
                  >
                    <div className="admin-jobs-panel__stat-value">{stat.value}</div>
                    <div className="admin-jobs-panel__stat-label">{stat.label}</div>
                  </div>
                ))}
              </div>
              <Stack direction="row" spacing={1.5} className="admin-jobs-panel__export-row">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadRoundedIcon />}
                  onClick={() => downloadJsonReport(jobState.result, jobState.mode || "dry-run")}
                >
                  Export JSON report
                </Button>
              </Stack>
            </div>

            {invalidExamples.length > 0 && (
              <div className="admin-jobs-panel__examples">
                <Typography variant="subtitle2" className="admin-jobs-panel__examples-title">
                  First flagged invalid examples
                </Typography>
                <div className="admin-jobs-panel__examples-list">
                  {invalidExamples.map((item, index) => (
                    <div
                      key={`${item.id}-${item.field}-${index}`}
                      className="admin-jobs-panel__examples-row"
                    >
                      <strong>{item.trackingNumber || item.id}</strong>: {item.field} ={" "}
                      {formatListValue(item.value)}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {sampleRows.length > 0 ? (
              <div className="admin-jobs-panel__sample-list">
                {sampleRows.map((item) => (
                  <div key={item.id} className="admin-jobs-panel__sample-row">
                    <div className="admin-jobs-panel__sample-title">
                      <strong>{item.trackingNumber || item.id}</strong>
                    </div>
                    <div className="admin-jobs-panel__sample-copy admin-jobs-panel__sample-copy--summary">
                      {describeRowOutcome(item)}
                    </div>
                    <div className="admin-jobs-panel__sample-copy">
                      Theme: {formatListValue(item.before?.theme)}
                    </div>
                    {item.actions?.removedTheme && (
                      <div className="admin-jobs-panel__sample-copy admin-jobs-panel__sample-copy--warning">
                        Theme will be removed after topic is confirmed.
                      </div>
                    )}
                    <div className="admin-jobs-panel__sample-copy">
                      Topic: {formatListValue(item.before?.topic)} -> {formatListValue(item.after?.topic)}
                    </div>
                    <div className="admin-jobs-panel__sample-copy">
                      Speciality: {formatListValue(item.before?.speciality)} -> {formatListValue(item.after?.speciality)}
                    </div>
                    <div className="admin-jobs-panel__sample-copy">
                      Presentation: {formatListValue(item.before?.presentation_type)} -> {formatListValue(item.after?.presentation_type)}
                    </div>
                    {Array.isArray(item.invalidFields) && item.invalidFields.length > 0 && (
                      <div className="admin-jobs-panel__sample-copy admin-jobs-panel__sample-copy--warning">
                        Invalid: {item.invalidFields.map((field) => `${field.field}=${formatListValue(field.value)}`).join(", ")}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <Typography variant="body2" className="admin-jobs-panel__empty">
                No affected submissions found.
              </Typography>
            )}
          </Box>
        )}
      </div>
    </div>
  );
};

export default AdminJobsPanel;
